// /**
//  * Centers - list
//  */

// React
import React, {useContext} from 'react';
import {Link} from 'react-router-dom';

// Context
import AuthContext from '../../../authContext';

// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
} from '@material-ui/core';

// Components
import Can from '../../../components/Can';

// Helper
import {formaterPhoneNumber} from '../../../utils/helpers';

// Type
import {Center} from '../interfaces';
import RonadIcons from '../../../components/iconComponent';

type Data = Center;
type Props = {
  data: any;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpenConfirmActive: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      textAlign: 'left',
      backgroundColor: '#D5DADD !important',
      color: '#212121',
    },
    body: {
      textAlign: 'left',
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    hover: {
      '&:hover': {
        backgroundColor: '#F8F9FA !important',
      },
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells: HeadCell[] = [
  {
    id: 'name',
    label: 'نام مرکز',
  },
  {
    id: 'id',
    label: 'شناسه مرکز',
  },

  {id: 'phoneNumber', label: 'شماره تماس مرکز'},
  {id: 'province', label: ' استان'},
  {id: 'city', label: 'شهر'},
  {id: 'address', label: 'آدرس مرکز'},
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />

        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              IconComponent={() => (
                <RonadIcons
                  name={
                    orderBy === headCell.id && order === 'desc'
                      ? 'chevronDown'
                      : 'chevronUp'
                  }
                  style={{marginRight: '8px'}}
                  size={0.7}
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);
  const {handleOpenConfirmDisable, handleOpenConfirmActive, handleOpen} = props;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow className={classes.hover} key={index}>
                    <StyledTableCell align="center">
                      {' '}
                      <span
                        className={classes.dot}
                        style={{
                          backgroundColor:
                            row.status === 'SUSPENDED' ||
                            row.status === 'REGISTERED'
                              ? theme.palette.error.main
                              : theme.palette.green.main,
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.name}
                    </StyledTableCell>

                    <StyledTableCell data-cy="id" align="center">
                      {row.id}
                    </StyledTableCell>
                    <StyledTableCell data-cy="phoneNumber" align="center">
                      {formaterPhoneNumber(row.phoneNumber)}
                    </StyledTableCell>
                    <StyledTableCell data-cy="province" align="center">
                      {row.province}
                    </StyledTableCell>
                    <StyledTableCell data-cy="city" align="center">
                      {row.city}
                    </StyledTableCell>
                    <StyledTableCell data-cy="address" align="center">
                      {row.address}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Link to={`/centerdetails/${row?.id}`}>
                        <IconButton
                          aria-label="details"
                          size="small"
                          title="جزئیات مرکز"
                          data-cy="details"
                        >
                          <RonadIcons name='documentOutline' className='text-green'/>
                        </IconButton>
                      </Link>
                      <Can
                        role={authContext.user.role}
                        perform="centers:editDetails"
                        yes={() => (
                          <Link to={`/centeredit/${row?.id}`}>
                            <IconButton
                              aria-label="edit"
                              size="small"
                              title="ویرایش"
                              data-cy="edit"
                            >
                              <RonadIcons name='pencil' />
                            </IconButton>
                          </Link>
                        )}
                      />{' '}
                      <Can
                        role={authContext.user.role}
                        perform="centers:disableCenter"
                        yes={() => (
                          <>
                            {row.status === 'SUSPENDED' ? (
                              <IconButton
                                aria-label="cancele"
                                title=" فعال کردن مرکز "
                                size="small"
                                onClick={() => handleOpenConfirmActive(row.id)}
                                data-cy="activate"
                              >
                                <RonadIcons name='check' size={0.8} className='text-green'/>
                              </IconButton>
                            ) : (
                              <IconButton
                                aria-label="cancele"
                                title="غیر فعال کردن مرکز "
                                size="small"
                                onClick={() => handleOpenConfirmDisable(row.id)}
                                data-cy="activate"
                              >
                                <RonadIcons name="times" className='text-danger'  size={0.8}/>
                              </IconButton>
                            )}
                          </>
                        )}
                      />
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
